import { useEffect, useState } from 'react';
import { useContracts } from './useContract';

// Module-level cache — tiers rarely change; keep them in memory across navigations.
// Cache TTL means changes from admin propagate within a short window even without explicit invalidation.
const CACHE_TTL_MS = 30_000; // 30s
const CHANGE_EVENT = 'xterra:package-tiers-change';

let CACHE = null;
let CACHE_AT = 0;
let INFLIGHT = null;

export function invalidatePackageTiersCache() {
  CACHE = null;
  CACHE_AT = 0;
  INFLIGHT = null;
  // Broadcast so every mounted usePackageTiers() consumer refetches immediately.
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(CHANGE_EVENT));
  }
}

/**
 * Fetch the 9 package tiers from storage.
 * - Cached module-level so tab switches don't re-fetch
 * - Cache auto-expires after CACHE_TTL_MS so admin updates propagate
 * - Multi-component calls dedupe onto one in-flight RPC
 * - Listens for invalidation events so admin edits show immediately
 */
export function usePackageTiers() {
  const { read } = useContracts();
  const cacheFresh = CACHE && (Date.now() - CACHE_AT) < CACHE_TTL_MS;
  const [tiers, setTiers] = useState(cacheFresh ? CACHE : []);
  const [loading, setLoading] = useState(!cacheFresh);
  const [tick, setTick] = useState(0);

  // Force a refetch whenever an invalidation event fires.
  useEffect(() => {
    const bump = () => setTick((t) => t + 1);
    window.addEventListener(CHANGE_EVENT, bump);
    return () => window.removeEventListener(CHANGE_EVENT, bump);
  }, []);

  useEffect(() => {
    let mounted = true;
    const isFresh = CACHE && (Date.now() - CACHE_AT) < CACHE_TTL_MS;

    if (isFresh) {
      setTiers(CACHE);
      setLoading(false);
      return;
    }

    const promise = INFLIGHT || (INFLIGHT = read.storage.getPackageTiers()
      .then((data) => {
        CACHE = data;
        CACHE_AT = Date.now();
        INFLIGHT = null;
        return data;
      })
      .catch(() => {
        INFLIGHT = null;
        return CACHE || [];
      }));

    setLoading(true);
    promise.then((data) => {
      if (!mounted) return;
      setTiers(data);
      setLoading(false);
    });

    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick]);

  return { tiers, loading };
}
